import type { ShopperProducts } from '@/scapi';
import { getMenuVisibleChildCategories, hasSubcategories, isCategoryShownInMenu } from './utils';

export const BRANDS_CATEGORY_ID = 'brands';

/** Storefront path for the brand directory landing page. */
export const BRANDS_DIRECTORY_PATH = '/brands';

export type BrandsCuratedVariant = 'affordable' | 'bestselling' | 'new';

type BrandsCustomAttributeKey = 'c_defaultTopBrands' | 'c_bestSellingBrands' | 'c_spaceTopBrands';

const CURATED_ATTRIBUTE_BY_VARIANT: Record<BrandsCuratedVariant, BrandsCustomAttributeKey> = {
    affordable: 'c_defaultTopBrands',
    bestselling: 'c_bestSellingBrands',
    new: 'c_spaceTopBrands',
};

export function findBrandsCategory(
    categories: ShopperProducts.schemas['Category'][]
): ShopperProducts.schemas['Category'] | undefined {
    return categories.find((category) => category.id === BRANDS_CATEGORY_ID);
}

/**
 * Prefer the deferred brands tree from `/resource/burger-menu-navigation` (level-2 fetch with
 * SCAPI custom attributes and letter-group children) over the shallow root category entry.
 */
export function resolveBrandsCategoryForBurgerMenu(
    rootCategories: ShopperProducts.schemas['Category'][],
    deferredCategories?: ShopperProducts.schemas['Category'][]
): ShopperProducts.schemas['Category'] | undefined {
    const fromDeferred = deferredCategories?.find((category) => category.id === BRANDS_CATEGORY_ID);
    const fromRoot = findBrandsCategory(rootCategories);

    if (fromDeferred) {
        return fromDeferred;
    }

    return fromRoot;
}

export function getBrandSectionId(letterCategoryId: string): string {
    return `burger-menu-brand-section-${letterCategoryId}`;
}

function normalizeBrandReferenceKey(reference: string): string {
    return reference.trim().toLowerCase();
}

function resolveBrandAttributeEntry(
    lookup: Map<string, ShopperProducts.schemas['Category']>,
    entry: unknown
): ShopperProducts.schemas['Category'] | undefined {
    if (typeof entry === 'string' && entry.length > 0) {
        return lookup.get(normalizeBrandReferenceKey(entry));
    }

    if (typeof entry === 'object' && entry !== null) {
        const record = entry as Record<string, unknown>;

        if (typeof record.id === 'string' && record.id.length > 0) {
            const resolvedById = lookup.get(normalizeBrandReferenceKey(record.id));

            if (resolvedById) {
                return resolvedById;
            }

            return entry as ShopperProducts.schemas['Category'];
        }

        if (typeof record.name === 'string' && record.name.length > 0) {
            return lookup.get(normalizeBrandReferenceKey(record.name));
        }
    }

    return undefined;
}

function getCuratedBrandEntries(
    category: ShopperProducts.schemas['Category'],
    attributeKey: BrandsCustomAttributeKey
): unknown[] {
    const value = category[attributeKey as keyof ShopperProducts.schemas['Category']];

    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value === 'string' && value.length > 0) {
        return [value];
    }

    return [];
}

function registerBrandCategoryLookupEntry(
    lookup: Map<string, ShopperProducts.schemas['Category']>,
    category: ShopperProducts.schemas['Category']
): void {
    const idKey = normalizeBrandReferenceKey(category.id);

    if (!lookup.has(idKey)) {
        lookup.set(idKey, category);
    }

    const name = category.name?.trim();

    if (name) {
        const nameKey = normalizeBrandReferenceKey(name);

        if (!lookup.has(nameKey)) {
            lookup.set(nameKey, category);
        }
    }
}

export function buildBrandCategoryLookup(
    brandsCategory: ShopperProducts.schemas['Category']
): Map<string, ShopperProducts.schemas['Category']> {
    const lookup = new Map<string, ShopperProducts.schemas['Category']>();

    const visit = (category: ShopperProducts.schemas['Category']) => {
        registerBrandCategoryLookupEntry(lookup, category);
        category.categories?.forEach(visit);
    };

    brandsCategory.categories?.forEach(visit);

    return lookup;
}

/**
 * Resolves brand categories from SCAPI custom-attribute values that may be category IDs
 * (e.g. `drunk-elephant`) or display names (e.g. `Charlotte Tilbury`).
 */
export function resolveBrandsByReferences(
    brandsCategory: ShopperProducts.schemas['Category'],
    references: string[]
): ShopperProducts.schemas['Category'][] {
    const lookup = buildBrandCategoryLookup(brandsCategory);

    return references
        .map((reference) => resolveBrandAttributeEntry(lookup, reference))
        .filter((category): category is ShopperProducts.schemas['Category'] => Boolean(category));
}

export function resolveBrandsByIds(
    brandsCategory: ShopperProducts.schemas['Category'],
    ids: string[]
): ShopperProducts.schemas['Category'][] {
    return resolveBrandsByReferences(brandsCategory, ids);
}

export function getCuratedBrandCategories(
    brandsCategory: ShopperProducts.schemas['Category'],
    variant: BrandsCuratedVariant
): ShopperProducts.schemas['Category'][] {
    const attributeKey = CURATED_ATTRIBUTE_BY_VARIANT[variant];
    const entries = getCuratedBrandEntries(brandsCategory, attributeKey);
    const lookup = buildBrandCategoryLookup(brandsCategory);

    return entries
        .map((entry) => resolveBrandAttributeEntry(lookup, entry))
        .filter((category): category is ShopperProducts.schemas['Category'] => Boolean(category))
        .filter(isCategoryShownInMenu);
}

/**
 * Letter-group categories are structural; only leaf brand categories require `c_showInMenu`.
 */
export function filterBrandsCategoryTreeForMenu(
    brandsCategory: ShopperProducts.schemas['Category']
): ShopperProducts.schemas['Category'] {
    return {
        ...brandsCategory,
        categories: brandsCategory.categories?.map((letterCategory) => ({
            ...letterCategory,
            categories: getMenuVisibleChildCategories(letterCategory),
        })),
    };
}

function isHashLetterCategory(category: ShopperProducts.schemas['Category']): boolean {
    const label = (category.name ?? category.id).trim();

    return label === '#';
}

export function compareBrandLetterCategories(
    left: ShopperProducts.schemas['Category'],
    right: ShopperProducts.schemas['Category']
): number {
    const leftIsHash = isHashLetterCategory(left);
    const rightIsHash = isHashLetterCategory(right);

    if (leftIsHash && !rightIsHash) {
        return 1;
    }

    if (!leftIsHash && rightIsHash) {
        return -1;
    }

    return (left.name ?? left.id).localeCompare(right.name ?? right.id, undefined, { sensitivity: 'base' });
}

export function getBrandLetterCategories(
    brandsCategory: ShopperProducts.schemas['Category']
): ShopperProducts.schemas['Category'][] {
    return [...(brandsCategory.categories ?? [])].sort(compareBrandLetterCategories);
}

export function isBrandGridLetterEnabled(letterCategory: ShopperProducts.schemas['Category']): boolean {
    if (letterCategory.online === false) {
        return false;
    }

    return hasSubcategories(letterCategory);
}
